import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
const resources = {
  en: {
    translation: {
      // Common
      common: {
        loading: 'Loading...',
        error: 'Error',
        success: 'Success',
        cancel: 'Cancel',
        confirm: 'Confirm',
        save: 'Save',
        delete: 'Delete',
        edit: 'Edit',
        back: 'Back',
        next: 'Next',
        close: 'Close',
        or: 'or',
        allRightsReserved: 'All rights reserved.',
        notifications: 'Notifications',
        markAsRead: 'Mark as read',
        viewAll: 'View all',
        noNotifications: 'No notifications',
      },
      
      // Auth
      auth: {
        login: 'Login',
        logout: 'Logout',
        signInWithGoogle: 'Continue with Google',
        welcomeBack: 'Welcome back!',
        loginDescription: 'Sign in to continue generating professional images',
        createAccount: 'Create Account',
        createAccountDescription: 'Create your account to start generating amazing images',
        email: 'Email',
        emailPlaceholder: 'your@email.com',
        password: 'Password',
        name: 'Name',
        namePlaceholder: 'Your name',
        whatsapp: 'WhatsApp',
        tagline: 'Transform your product photos with AI',
        alreadyHaveAccount: 'Already have an account?',
        dontHaveAccount: "Don't have an account?",
        verifyEmail: 'Verify your email',
        verifyEmailDescription: 'We sent a verification link to your email. Check your inbox and spam folder, then click the link to activate your account.',
        alreadyVerified: 'I already verified',
        resendEmail: 'Resend verification email',
        resendIn: 'Resend in {{seconds}}s',
        verificationResent: 'Verification email resent!',
        forgotPassword: 'Forgot password?',
        forgotPasswordDescription: "Enter your email and we'll send you a reset link",
        sendResetLink: 'Send reset link',
        resetSent: 'Email sent!',
        resetSentDescription: 'Check your inbox for the password reset link.',
        backToLogin: 'Back to login',
        errors: {
          userNotFound: 'User not found',
          wrongPassword: 'Incorrect password',
          emailInUse: 'Email already in use',
          weakPassword: 'Password too weak (minimum 6 characters)',
          loginFailed: 'Login failed. Please try again.',
          googleFailed: 'Google login failed. Please try again.',
          resendFailed: 'Failed to resend email. Try again later.',
          resetFailed: 'Failed to send reset email. Try again.',
          notVerifiedYet: 'Email not verified yet. Check your inbox and spam folder.',
        },
      },
      
      // Navigation
      nav: {
        dashboard: 'Dashboard',
        newGeneration: 'New Generation',
        generations: 'Generations',
        settings: 'Settings',
      },
      
      // Dashboard
      dashboard: {
        title: 'Your Generations',
        subtitle: 'Overview of your generations',
        empty: 'No generations yet',
        emptyDescription: 'Start by creating your first AI-generated image',
        createFirst: 'Create first generation',
        viewDetails: 'View Details',
        totalGenerations: 'Total Generations',
        completed: 'Completed',
        processing: 'Processing',
        recentGenerations: 'Recent Generations',
        status: {
          pending: 'Pending',
          processing: 'Processing',
          completed: 'Completed',
          failed: 'Failed',
        },
      },
      
      // New Generation
      newGeneration: {
        title: 'Create New Generation',
        subtitle: 'Transform your product photo with AI',
        configuration: 'Configuration',
        configDescription: 'Fill in the fields below to generate your professional images',
        productName: 'Product Name',
        productNamePlaceholder: 'E.g.: Silver earring, Gold necklace, White sneakers...',
        productNameHint: 'Enter the product name or description',
        additionalInstructions: 'Additional instructions (optional)',
        additionalInstructionsPlaceholder: 'E.g.: Show the earring on a female ear, light background...',
        productContextHint: 'Describe your product so the AI generates more accurate photos',
        uploadImage: 'Product Image',
        clickToUpload: 'Click to upload',
        dragOrClick: 'Drag and drop or click to upload',
        dropHere: 'Drop the image here',
        fileTypes: 'PNG, JPG, WebP, AVIF or HEIC up to 10MB',
        imageUrl: 'Image URL',
        imageUrlPlaceholder: 'https://example.com/product.jpg',
        imageUrlHint: 'Paste the URL of your product image',
        preview: 'Preview',
        selectStyle: 'Generation Style',
        selectStylePlaceholder: 'Select a style',
        selectStyleHint: 'Choose the style that best suits your product',
        styles: {
          CONTEXTO_USO: 'In-Use Context',
          EDITORIAL_LUXO: 'Luxury Editorial',
          LIFESTYLE_MINIMAL: 'Minimal Lifestyle',
          STUDIO_PROFISSIONAL: 'Professional Studio',
        },
        stylesDesc: {
          context: 'Product in its natural use scenario',
          luxury: 'High-end editorial magazine style',
          lifestyle: 'Clean Scandinavian aesthetic',
          studio: 'Commercial studio lighting',
        },
        generate: 'Generate Photos',
        generating: 'Generating your photos...',
        freeRemaining: 'Free generation ({{remaining}} of 2 remaining)',
        creditWillBeUsed: '1 credit will be used',
        freeExhausted: 'Your free generations are used up',
        dragAndDrop: 'Drag and drop an image here, or click to select',
        imageUploaded: 'Image uploaded successfully',
        validation: {
          productNameMin: 'Product name must be at least 2 characters',
          productNameMax: 'Product name must be at most 100 characters',
          styleRequired: 'Please select a style',
          imageRequired: 'Please upload an image or provide a URL',
        },
        errors: {
          fillAllFields: 'Please fill all fields',
          productNameRequired: 'Product name is required',
          imageRequired: 'Please upload an image or provide a URL',
          styleRequired: 'Please select a style',
          generationFailed: 'Failed to create generation',
          invalidImage: 'Invalid image',
          insufficientCredits: 'Your 2 free generations are used up. Please purchase credits to continue.',
          fileTooLarge: 'File is too large. Maximum size is 10MB.',
          invalidFileType: 'Invalid file type. Only PNG, JPG, WebP, AVIF and HEIC are allowed.',
          uploadFailed: 'Failed to upload file.',
        },
      },
      
      // Generation Detail
      generationDetail: {
        title: 'Generation Details',
        productName: 'Product Name',
        style: 'Style',
        status: 'Status',
        createdAt: 'Created At',
        originalImage: 'Original Image',
        generatedImages: 'Generated Images',
        noImages: 'No images generated',
        download: 'Download',
        downloadAll: 'Download All',
        deleteGeneration: 'Delete Generation',
        confirmDelete: 'Are you sure you want to delete this generation?',
        deleteSuccess: 'Generation deleted successfully',
        deleteFailed: 'Failed to delete generation',
        loading: 'Loading generation...',
        notFound: 'Generation not found',
        backToDashboard: 'Back to Dashboard',
        imagesReady: '{{count}} images ready for download',
        generating: 'Generating your images with AI...',
        generationFailed: 'Generation failed',
        waitingProcessing: 'Waiting for processing',
        generatingImages: 'Generating your images...',
        takeFewSeconds: 'This may take a few seconds',
        failedToGenerate: 'Failed to generate images',
        tryAgainLater: 'Try again later',
      },
      
      // Style Guide
      styleGuide: {
        title: 'Style Guide',
        subtitle: 'Explore our generation styles to find the perfect look for your product.',
        bestFor: 'Best for:',
        useCases: {
          marketplace: 'E-commerce listings, Amazon, Mercado Livre',
          lifestyle: 'Instagram, Social Media, Modern Brands',
          natural: 'Outdoor products, Eco-friendly brands',
          studio: 'Professional catalogs, Corporate presentations',
          luxury: 'High-end brands, Jewelry, Perfume',
          flatlay: 'Social Media, Pinterest, Product Compositions',
          context: 'Product demonstration, Functional showcase',
          shadows: 'Modern branding, Minimalist aesthetic',
          detail: 'Showcasing quality, textures, and materials',
          general: 'General product showcase',
        },
      },
      
      // Credits
      credits: {
        title: 'Your Credits',
        available: 'Available Credits',
        buyMore: 'Buy More Credits',
        buyCredits: 'Buy 50 Credits',
        creditInfo: '1 credit = 1 generation (3 variations)',
        creditsRemaining: '{{count}} credit remaining',
        creditsRemaining_plural: '{{count}} credits remaining',
        insufficientCredits: 'Insufficient Credits',
        insufficientCreditsMessage: 'You need more credits to generate images',
        purchaseCredits: 'Purchase Credits',
      },
      
      // Payment
      payment: {
        success: 'Payment Successful!',
        successMessage: 'Your credits have been added to your account',
        cancel: 'Payment Cancelled',
        cancelMessage: 'Your payment was cancelled. No charges were made.',
        returnToDashboard: 'Return to Dashboard',
        processing: 'Processing payment...',
      },
      
      // Feedback
      feedback: {
        title: 'How was the generation?',
        subtitle: 'Your feedback helps us improve the quality of generated images',
        satisfied: 'Satisfied',
        unsatisfied: 'Not satisfied',
        ratingLabel: 'Rate this generation',
        commentLabel: 'Tell us what went wrong (optional)',
        commentPlaceholder: 'Example: The background doesn\'t match the product...',
        requestRefund: 'Request credit refund',
        refundPolicy: 'Refund requests are reviewed within 24 hours',
        submitFeedback: 'Submit Feedback',
        thankYou: 'Thank you for your feedback!',
        rateGeneration: 'Rate Generation',
        feedbackSent: 'Feedback sent',
        awaitingReview: 'Awaiting review',
        creditRefunded: 'Credit refunded',
        refundRejected: 'Refund request rejected',
        errors: {
          selectRating: 'Please select a rating',
          alreadySubmitted: 'You have already submitted feedback for this generation',
          submitFailed: 'Failed to submit feedback',
        },
      },
      
      // Errors
      errors: {
        somethingWentWrong: 'Something went wrong',
        tryAgain: 'Please try again',
        unauthorized: 'Unauthorized',
        notFound: 'Not found',
        serverError: 'Server error',
      },
    },
  },
  
  'pt-BR': {
    translation: {
      // Common
      common: {
        loading: 'Carregando...',
        error: 'Erro',
        success: 'Sucesso',
        cancel: 'Cancelar',
        confirm: 'Confirmar',
        save: 'Salvar',
        delete: 'Excluir',
        edit: 'Editar',
        back: 'Voltar',
        next: 'Próximo',
        close: 'Fechar',
        or: 'ou',
        allRightsReserved: 'Todos os direitos reservados.',
        notifications: 'Notificações',
        markAsRead: 'Marcar como lida',
        viewAll: 'Ver todas',
        noNotifications: 'Não há notificações',
      },

      // Auth
      auth: {
        login: 'Entrar',
        logout: 'Sair',
        signInWithGoogle: 'Continuar com Google',
        welcomeBack: 'Bem-vindo de volta!',
        loginDescription: 'Faça login para continuar gerando imagens profissionais',
        createAccount: 'Criar Conta',
        createAccountDescription: 'Crie sua conta para começar a gerar imagens incríveis',
        email: 'Email',
        emailPlaceholder: 'seu@email.com',
        password: 'Senha',
        name: 'Nome',
        namePlaceholder: 'Seu nome',
        whatsapp: 'WhatsApp',
        tagline: 'Transforme suas fotos de produtos com IA',
        alreadyHaveAccount: 'Já tem uma conta?',
        dontHaveAccount: 'Não tem uma conta?',
        verifyEmail: 'Verifique seu email',
        verifyEmailDescription: 'Enviamos um link de verificação para seu email. Verifique sua caixa de entrada e a pasta de spam, depois clique no link para ativar sua conta.',
        alreadyVerified: 'Já verifiquei',
        resendEmail: 'Reenviar email de verificação',
        resendIn: 'Reenviar em {{seconds}}s',
        verificationResent: 'Email de verificação reenviado!',
        forgotPassword: 'Esqueceu a senha?',
        forgotPasswordDescription: 'Digite seu email e enviaremos um link para redefinir sua senha',
        sendResetLink: 'Enviar link de redefinição',
        resetSent: 'Email enviado!',
        resetSentDescription: 'Verifique sua caixa de entrada para o link de redefinição de senha.',
        backToLogin: 'Voltar ao login',
        errors: {
          userNotFound: 'Usuário não encontrado',
          wrongPassword: 'Senha incorreta',
          emailInUse: 'Email já está em uso',
          weakPassword: 'Senha muito fraca (mínimo 6 caracteres)',
          loginFailed: 'Erro ao fazer login. Tente novamente.',
          googleFailed: 'Erro ao fazer login com Google. Tente novamente.',
          resendFailed: 'Falha ao reenviar email. Tente novamente mais tarde.',
          resetFailed: 'Falha ao enviar email de redefinição. Tente novamente.',
          notVerifiedYet: 'Email ainda não verificado. Verifique sua caixa de entrada e a pasta de spam.',
        },
      },
      
      // Navigation
      nav: {
        dashboard: 'Dashboard',
        newGeneration: 'Nova Geração',
        generations: 'Gerações',
        settings: 'Configurações',
      },
      
      // Dashboard
      dashboard: {
        title: 'Suas Gerações',
        subtitle: 'Visão geral das suas gerações',
        empty: 'Nenhuma geração ainda',
        emptyDescription: 'Comece criando sua primeira geração de imagens com IA',
        createFirst: 'Criar primeira geração',
        viewDetails: 'Ver Detalhes',
        totalGenerations: 'Total de Gerações',
        completed: 'Concluídas',
        processing: 'Em Processamento',
        recentGenerations: 'Gerações Recentes',
        status: {
          pending: 'Pendente',
          processing: 'Processando',
          completed: 'Concluído',
          failed: 'Falhou',
        },
      },

      feedback: {
        title: 'Como foi a geração?',
        subtitle: 'Seu feedback nos ajuda a melhorar a qualidade das imagens geradas',
        satisfied: 'Satisfeito',
        unsatisfied: 'Não satisfeito',
        ratingLabel: 'Avalie esta geração',
        commentLabel: 'Conte-nos o que deu errado (opcional)',
        commentPlaceholder: 'Exemplo: O fundo não corresponde ao produto...',
        requestRefund: 'Reembolsar créditos',
        refundPolicy: 'Requisições de reembolso são analisadas em até 24 horas',
        submitFeedback: 'Enviar Feedback',
        thankYou: 'Obrigado pelo seu feedback!',
        rateGeneration: 'Avaliar',
        feedbackSent: 'Feedback enviado',
        awaitingReview: 'Aguardando revisão',
        creditRefunded: 'Créditos reembolsados',
        refundRejected: 'Requisição de reembolso rejeitada',
        errors: {
          selectRating: 'Por favor, selecione uma avaliação',
          alreadySubmitted: 'Você já enviou feedback para esta geração',
          submitFailed: 'Falha ao enviar feedback',
        },
      },
      
      // New Generation
      newGeneration: {
        title: 'Nova Geração',
        subtitle: 'Transforme sua foto de produto com IA',
        configuration: 'Configuração',
        configDescription: 'Preencha os campos abaixo para gerar suas imagens profissionais',
        productName: 'Nome do Produto',
        productNamePlaceholder: 'Ex: Brinco de prata, Colar dourado, Tenis branco...',
        productNameHint: 'Digite o nome ou descrição do produto',
        additionalInstructions: 'Instrucoes adicionais (opcional)',
        additionalInstructionsPlaceholder: 'Ex: Mostrar o brinco em uma orelha feminina, fundo claro...',
        productContextHint: 'Descreva seu produto para que a IA gere fotos mais precisas',
        uploadImage: 'Imagem do Produto',
        clickToUpload: 'Clique para fazer upload',
        dragOrClick: 'Arraste e solte ou clique para fazer upload',
        dropHere: 'Solte a imagem aqui',
        fileTypes: 'PNG, JPG, WebP, AVIF ou HEIC até 10MB',
        imageUrl: 'URL da Imagem',
        imageUrlPlaceholder: 'https://exemplo.com/produto.jpg',
        imageUrlHint: 'Cole a URL de uma imagem do seu produto',
        preview: 'Preview',
        selectStyle: 'Estilo de Geração',
        selectStylePlaceholder: 'Selecione um estilo',
        selectStyleHint: 'Escolha o estilo que melhor se adequa ao seu produto',
        styles: {
          CONTEXTO_USO: 'Contexto de Uso',
          EDITORIAL_LUXO: 'Editorial de Luxo',
          LIFESTYLE_MINIMAL: 'Lifestyle Minimalista',
          STUDIO_PROFISSIONAL: 'Estudio Profissional',
        },
        stylesDesc: {
          context: 'Produto no cenario real de uso',
          luxury: 'Estilo editorial de revista premium',
          lifestyle: 'Estetica escandinava minimalista',
          studio: 'Iluminacao profissional de estudio',
        },
        generate: 'Gerar Fotos',
        generating: 'Gerando suas fotos...',
        freeRemaining: 'Geracao gratuita ({{remaining}} de 2 restantes)',
        creditWillBeUsed: '1 credito sera usado',
        freeExhausted: 'Suas geracoes gratuitas acabaram',
        dragAndDrop: 'Arraste e solte uma imagem aqui, ou clique para selecionar',
        imageUploaded: 'Imagem enviada com sucesso',
        validation: {
          productNameMin: 'Nome do produto deve ter pelo menos 2 caracteres',
          productNameMax: 'Nome do produto deve ter no maximo 100 caracteres',
          styleRequired: 'Por favor, selecione um estilo',
          imageRequired: 'Por favor, envie uma imagem ou forneça uma URL',
        },
        errors: {
          fillAllFields: 'Preencha todos os campos',
          productNameRequired: 'Nome do produto é obrigatório',
          imageRequired: 'Por favor, envie uma imagem ou forneça uma URL',
          styleRequired: 'Por favor, selecione um estilo',
          generationFailed: 'Erro ao criar geração',
          invalidImage: 'Imagem inválida',
          insufficientCredits: 'Suas 2 geracoes gratuitas acabaram. Compre creditos para continuar.',
          fileTooLarge: 'Arquivo muito grande. Tamanho máximo é 10MB.',
          invalidFileType: 'Tipo de arquivo inválido. Apenas PNG, JPG, WebP, AVIF e HEIC são permitidos.',
          uploadFailed: 'Falha ao enviar arquivo.',
        },
      },

      // Generation Detail
      generationDetail: {
        title: 'Detalhes da Geração',
        productName: 'Nome do Produto',
        style: 'Estilo',
        status: 'Status',
        createdAt: 'Criado em',
        originalImage: 'Imagem Original',
        generatedImages: 'Imagens Geradas',
        noImages: 'Nenhuma imagem gerada',
        download: 'Baixar',
        downloadAll: 'Baixar Todas',
        deleteGeneration: 'Excluir Geração',
        confirmDelete: 'Tem certeza que deseja excluir esta geração?',
        deleteSuccess: 'Geração excluída com sucesso',
        deleteFailed: 'Falha ao excluir geração',
        loading: 'Carregando geração...',
        notFound: 'Geração não encontrada',
        backToDashboard: 'Voltar ao Painel',
        imagesReady: '{{count}} imagens prontas para download',
        generating: 'Gerando suas imagens com IA...',
        generationFailed: 'Falha na geração',
        waitingProcessing: 'Aguardando processamento',
        generatingImages: 'Gerando suas imagens...',
        takeFewSeconds: 'Isso pode levar alguns segundos',
        failedToGenerate: 'Falha ao gerar imagens',
        tryAgainLater: 'Tente novamente mais tarde',
      },
      
      // Style Guide
      styleGuide: {
        title: 'Guia de Estilos',
        subtitle: 'Explore nossos estilos de geração para encontrar o visual perfeito para seu produto.',
        bestFor: 'Ideal para:',
        useCases: {
          marketplace: 'Anúncios de e-commerce, Amazon, Mercado Livre',
          lifestyle: 'Instagram, Redes Sociais, Marcas Modernas',
          natural: 'Produtos outdoor, Marcas sustentáveis',
          studio: 'Catálogos profissionais, Apresentações corporativas',
          luxury: 'Marcas de luxo, Joias, Perfumes',
          flatlay: 'Redes Sociais, Pinterest, Composições de produto',
          context: 'Demonstração de produto, Showcase funcional',
          shadows: 'Branding moderno, Estética minimalista',
          detail: 'Destacar qualidade, texturas e materiais',
          general: 'Exibição geral de produtos',
        },
      },
      
      // Credits
      credits: {
        title: 'Créditos',
        buyCredits: 'Comprar 50 Créditos',
        available: 'Créditos Disponíveis',
        creditInfo: '1 crédito = 1 geração (3 variações)',
        buyMore: 'Comprar Mais Créditos',
        creditsRemaining: '{{count}} crédito restante',
        creditsRemaining_plural: '{{count}} créditos restantes',
        insufficientCredits: 'Créditos Insuficientes',
        insufficientCreditsMessage: 'Você precisa de mais créditos para gerar imagens',
        purchaseCredits: 'Comprar Créditos',
      },
      
      // Payment
      payment: {
        success: 'Pagamento Realizado!',
        successMessage: 'Seus créditos foram adicionados à sua conta',
        cancel: 'Pagamento Cancelado',
        cancelMessage: 'Seu pagamento foi cancelado. Nenhuma cobrança foi feita.',
        returnToDashboard: 'Voltar ao Painel',
        processing: 'Processando pagamento...',
      },
      
      // Errors
      errors: {
        somethingWentWrong: 'Algo deu errado',
        tryAgain: 'Por favor, tente novamente',
        unauthorized: 'Não autorizado',
        notFound: 'Não encontrado',
        serverError: 'Erro no servidor',
      },
    },
  },
  
  // Add 'pt' as alias for 'pt-BR'
  'pt': {
    translation: {
      // Common
      common: {
        loading: 'Carregando...',
        error: 'Erro',
        success: 'Sucesso',
        cancel: 'Cancelar',
        confirm: 'Confirmar',
        save: 'Salvar',
        delete: 'Excluir',
        edit: 'Editar',
        back: 'Voltar',
        next: 'Próximo',
        close: 'Fechar',
        or: 'ou',
        allRightsReserved: 'Todos os direitos reservados.',
      },

      // Auth
      auth: {
        login: 'Entrar',
        logout: 'Sair',
        signInWithGoogle: 'Continuar com Google',
        welcomeBack: 'Bem-vindo de volta!',
        loginDescription: 'Faça login para continuar gerando imagens profissionais',
        createAccount: 'Criar Conta',
        createAccountDescription: 'Crie sua conta para começar a gerar imagens incríveis',
        email: 'Email',
        emailPlaceholder: 'seu@email.com',
        password: 'Senha',
        name: 'Nome',
        namePlaceholder: 'Seu nome',
        whatsapp: 'WhatsApp',
        tagline: 'Transforme suas fotos de produtos com IA',
        alreadyHaveAccount: 'Já tem uma conta?',
        dontHaveAccount: 'Não tem uma conta?',
        verifyEmail: 'Verifique seu email',
        verifyEmailDescription: 'Enviamos um link de verificação para seu email. Verifique sua caixa de entrada e a pasta de spam, depois clique no link para ativar sua conta.',
        alreadyVerified: 'Já verifiquei',
        resendEmail: 'Reenviar email de verificação',
        resendIn: 'Reenviar em {{seconds}}s',
        verificationResent: 'Email de verificação reenviado!',
        forgotPassword: 'Esqueceu a senha?',
        forgotPasswordDescription: 'Digite seu email e enviaremos um link para redefinir sua senha',
        sendResetLink: 'Enviar link de redefinição',
        resetSent: 'Email enviado!',
        resetSentDescription: 'Verifique sua caixa de entrada para o link de redefinição de senha.',
        backToLogin: 'Voltar ao login',
        errors: {
          userNotFound: 'Usuário não encontrado',
          wrongPassword: 'Senha incorreta',
          emailInUse: 'Email já está em uso',
          weakPassword: 'Senha muito fraca (mínimo 6 caracteres)',
          loginFailed: 'Erro ao fazer login. Tente novamente.',
          googleFailed: 'Erro ao fazer login com Google. Tente novamente.',
          resendFailed: 'Falha ao reenviar email. Tente novamente mais tarde.',
          resetFailed: 'Falha ao enviar email de redefinição. Tente novamente.',
          notVerifiedYet: 'Email ainda não verificado. Verifique sua caixa de entrada e a pasta de spam.',
        },
      },
      
      // Navigation
      nav: {
        dashboard: 'Painel',
        newGeneration: 'Nova Geração',
        generations: 'Gerações',
        settings: 'Configurações',
      },
      
      // Dashboard
      dashboard: {
        title: 'Suas Gerações',
        subtitle: 'Visão geral das suas gerações',
        empty: 'Nenhuma geração ainda',
        emptyDescription: 'Comece criando sua primeira geração de imagens com IA',
        createFirst: 'Criar primeira geração',
        viewDetails: 'Ver Detalhes',
        totalGenerations: 'Total de Gerações',
        completed: 'Concluídas',
        processing: 'Em Processamento',
        recentGenerations: 'Gerações Recentes',
        status: {
          pending: 'Pendente',
          processing: 'Processando',
          completed: 'Concluído',
          failed: 'Falhou',
        },
      },
      
      // New Generation
      newGeneration: {
        title: 'Nova Geração',
        subtitle: 'Transforme sua foto de produto com IA',
        configuration: 'Configuração',
        configDescription: 'Preencha os campos abaixo para gerar suas imagens profissionais',
        productName: 'Nome do Produto',
        productNamePlaceholder: 'Ex: Brinco de prata, Colar dourado, Tenis branco...',
        productNameHint: 'Digite o nome ou descrição do produto',
        additionalInstructions: 'Instrucoes adicionais (opcional)',
        additionalInstructionsPlaceholder: 'Ex: Mostrar o brinco em uma orelha feminina, fundo claro...',
        productContextHint: 'Descreva seu produto para que a IA gere fotos mais precisas',
        uploadImage: 'Imagem do Produto',
        clickToUpload: 'Clique para fazer upload',
        dragOrClick: 'Arraste e solte ou clique para fazer upload',
        dropHere: 'Solte a imagem aqui',
        fileTypes: 'PNG, JPG, WebP, AVIF ou HEIC até 10MB',
        imageUrl: 'URL da Imagem',
        imageUrlPlaceholder: 'https://exemplo.com/produto.jpg',
        imageUrlHint: 'Cole a URL de uma imagem do seu produto',
        preview: 'Preview',
        selectStyle: 'Estilo de Geração',
        selectStylePlaceholder: 'Selecione um estilo',
        selectStyleHint: 'Escolha o estilo que melhor se adequa ao seu produto',
        styles: {
          CONTEXTO_USO: 'Contexto de Uso',
          EDITORIAL_LUXO: 'Editorial de Luxo',
          LIFESTYLE_MINIMAL: 'Lifestyle Minimalista',
          STUDIO_PROFISSIONAL: 'Estudio Profissional',
        },
        stylesDesc: {
          context: 'Produto no cenario real de uso',
          luxury: 'Estilo editorial de revista premium',
          lifestyle: 'Estetica escandinava minimalista',
          studio: 'Iluminacao profissional de estudio',
        },
        generate: 'Gerar Fotos',
        generating: 'Gerando suas fotos...',
        freeRemaining: 'Geracao gratuita ({{remaining}} de 2 restantes)',
        creditWillBeUsed: '1 credito sera usado',
        freeExhausted: 'Suas geracoes gratuitas acabaram',
        dragAndDrop: 'Arraste e solte uma imagem aqui, ou clique para selecionar',
        imageUploaded: 'Imagem enviada com sucesso',
        validation: {
          productNameMin: 'Nome do produto deve ter pelo menos 2 caracteres',
          productNameMax: 'Nome do produto deve ter no maximo 100 caracteres',
          styleRequired: 'Por favor, selecione um estilo',
          imageRequired: 'Por favor, envie uma imagem ou forneça uma URL',
        },
        errors: {
          fillAllFields: 'Preencha todos os campos',
          productNameRequired: 'Nome do produto é obrigatório',
          imageRequired: 'Por favor, envie uma imagem ou forneça uma URL',
          styleRequired: 'Por favor, selecione um estilo',
          generationFailed: 'Erro ao criar geração',
          invalidImage: 'Imagem inválida',
          insufficientCredits: 'Suas 2 geracoes gratuitas acabaram. Compre creditos para continuar.',
          fileTooLarge: 'Arquivo muito grande. Tamanho máximo é 10MB.',
          invalidFileType: 'Tipo de arquivo inválido. Apenas PNG, JPG, WebP, AVIF e HEIC são permitidos.',
          uploadFailed: 'Falha ao enviar arquivo.',
        },
      },

      // Generation Detail
      generationDetail: {
        title: 'Detalhes da Geração',
        productName: 'Nome do Produto',
        style: 'Estilo',
        status: 'Status',
        createdAt: 'Criado em',
        originalImage: 'Imagem Original',
        generatedImages: 'Imagens Geradas',
        noImages: 'Nenhuma imagem gerada ainda',
        download: 'Baixar',
        deleteGeneration: 'Excluir Geração',
        confirmDelete: 'Tem certeza que deseja excluir esta geração?',
        deleteSuccess: 'Geração excluída com sucesso',
        deleteFailed: 'Falha ao excluir geração',
      },
      
      // Credits
      credits: {
        title: 'Créditos',
        available: 'Créditos Disponíveis',
        buyMore: 'Comprar Mais Créditos',
        creditsRemaining: '{{count}} crédito restante',
        creditsRemaining_plural: '{{count}} créditos restantes',
        insufficientCredits: 'Créditos Insuficientes',
        insufficientCreditsMessage: 'Você precisa de mais créditos para gerar imagens',
        purchaseCredits: 'Comprar Créditos',
      },
      
      // Payment
      payment: {
        success: 'Pagamento Realizado!',
        successMessage: 'Seus créditos foram adicionados à sua conta',
        cancel: 'Pagamento Cancelado',
        cancelMessage: 'Seu pagamento foi cancelado. Nenhuma cobrança foi feita.',
        returnToDashboard: 'Voltar ao Painel',
        processing: 'Processando pagamento...',
      },
      
      // Errors
      errors: {
        somethingWentWrong: 'Algo deu errado',
        tryAgain: 'Por favor, tente novamente',
        unauthorized: 'Não autorizado',
        notFound: 'Não encontrado',
        serverError: 'Erro no servidor',
      },
    },
  },
};

i18n
  .use(LanguageDetector) // Detect user language
  .use(initReactI18next) // Pass i18n instance to react-i18next
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: ['en', 'pt-BR', 'pt'],
    nonExplicitSupportedLngs: true, // Allow 'pt' to map to 'pt-BR'
    
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    
    detection: {
      // Order of language detection
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    
    // Map 'pt' to 'pt-BR'
    load: 'currentOnly',
  });

export default i18n;
